import { Fragment, useState } from "react"
import { Tooltip } from "react-tooltip"
import { CellContext } from "@tanstack/react-table"
import classNames from "classnames"
import styled, { css } from "styled-components"

import { FeatureFlag, isFeatureEnabled } from "store/featureFlagsSlice"
import { useAppSelector } from "store/hooks"
import { ApiTypes, UITypes } from "types"
import ProductType from "types/enums/ProductType"
import { CellComponentProps } from "types/table/cellComponentProps"
import { ReactComponent as CwaIcon } from "assets/svg/cwa.svg"
import { ReactComponent as ThsIcon } from "assets/svg/humidity_sensor.svg"
import { ReactComponent as ThermostatIcon } from "assets/svg/thermostat.svg"
import { ReactComponent as WlsIcon } from "assets/svg/water_leak_sensor.svg"

import { getProductTypeLabel } from "utils/getProductTypeLabel"
import { isSerializedProductType } from "utils/productType"
import { OrderItemsModal } from "pages/contractor/Orders/OrderItemsModal"

import styles from "../index.module.scss"

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const ItemContainer = styled.div<{ isClickable: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  min-width: 42px;
  border-radius: 24px;
  gap: 4px;
  padding: 2px 4px;
  background-color: #ffffff;

  ${({ isClickable }) =>
    isClickable &&
    css`
      cursor: pointer;
      &:hover {
        opacity: 0.7;
      }
    `}
`

const IconWrapper = styled.div<{ isClickable: boolean; $accentColor?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;

  ${({ isClickable, $accentColor }) =>
    (isClickable || $accentColor) &&
    css`
      svg {
        path {
          fill: #2db2c8;
        }
      }
    `}
`

const Quantity = styled.span<{ isClickable: boolean }>`
  font-size: 14px;
  font-weight: 400;
  color: #0f172a;

  ${({ isClickable }) =>
    isClickable &&
    css`
      color: #2db2c8;
      text-decoration: underline;
    `}
`

const productTypeIcons: Record<ApiTypes.Model.OrderProductType, typeof ThermostatIcon> = {
  [ProductType.Thermostat]: ThermostatIcon,
  [ProductType.ThermostatV2]: ThermostatIcon,
  [ProductType.THS]: ThsIcon,
  [ProductType.WLS]: WlsIcon,
  [ProductType.CWA]: CwaIcon,
}

const getProductTypeIcon = (productType: ApiTypes.Model.OrderProductType) => productTypeIcons[productType] || CwaIcon

interface OrdersQuantityProps {
  cell: CellContext<UITypes.TableBodyRow, UITypes.TableBodyCell>
  cellComponentProps?: CellComponentProps
}

const OrdersQuantity = ({ cell, cellComponentProps }: OrdersQuantityProps) => {
  const items = cell.row.original.rowSettings.item as ApiTypes.Model.OrderNewItem[]
  const isVendor = cellComponentProps?.isVendor
  const isWHManager = cellComponentProps?.isWHManager
  const showV2 = useAppSelector(isFeatureEnabled(FeatureFlag.ThermV2Ordering))
  const [showOrderItemsModal, setShowOrderItemsModal] = useState(false)
  const orderItem = cell.row.original.rowSettings.item[0]
  const orderNumber = orderItem.id
  const showClickableQuantity = !!cell.row.parentId || cell.row.original.subRows?.length === 0
  const isExpanded = cell.row.getIsExpanded()
  if (!items || items.length === 0 || (isExpanded && items.length > 1)) {
    return null
  }

  const showDetails =
    ["packed", "shipping", "archived"].includes(orderItem.status) && isSerializedProductType(orderItem.product_type)
  const isPackedStatus = orderItem.status === "packed"

  const groupedItems = items.reduce(
    (acc, item) => {
      const productType = item.product_type
      if (!acc[productType]) {
        acc[productType] = 0
      }
      acc[productType] += item.quantity
      return acc
    },
    {} as Record<ApiTypes.Model.OrderProductType, number>,
  )

  const itemsToDisplay = Object.entries(groupedItems).map(([productType, quantity]) => ({
    productType: productType as ApiTypes.Model.OrderProductType,
    quantity,
  }))

  const handleOrderQuantityClick = () => {
    setShowOrderItemsModal(true)
  }

  if (itemsToDisplay.length === 0) {
    return null
  }

  const isClickable = showDetails && showClickableQuantity
  const tooltipId = `order-thermostat-quantity-${orderNumber}`

  return (
    <Container>
      {itemsToDisplay
        .sort((a, b) => b.productType.localeCompare(a.productType))
        .map(({ productType, quantity }) => {
          const Icon = getProductTypeIcon(productType)
          const key = [productType, orderNumber].join("-")
          const deviceTooltipId = `order-device-type-${productType}-${orderNumber}`
          const isContractorContext = !isVendor && !isWHManager
          const useContractorOverride = isContractorContext && !showV2
          const deviceLabel = getProductTypeLabel(productType, useContractorOverride)
          return (
            <Fragment key={key}>
              <ItemContainer
                data-tooltip-id={isClickable ? tooltipId : undefined}
                onClick={isClickable ? handleOrderQuantityClick : undefined}
                isClickable={isClickable}
              >
                <IconWrapper
                  isClickable={isClickable}
                  $accentColor={productType === ProductType.ThermostatV2}
                  data-tooltip-id={deviceTooltipId}
                >
                  <Icon />
                </IconWrapper>
                <Quantity isClickable={isClickable}>{quantity}</Quantity>
              </ItemContainer>
              {deviceLabel && (
                <Tooltip
                  id={deviceTooltipId}
                  variant="dark"
                  delayShow={500}
                  classNameArrow={styles.tooltipArrow}
                  className={classNames(styles.tooltip, styles.noBoxShadow)}
                >
                  {deviceLabel}
                </Tooltip>
              )}
              {isClickable && (
                <Tooltip
                  id={tooltipId}
                  variant="dark"
                  delayShow={200}
                  classNameArrow={styles.tooltipArrow}
                  className={classNames(styles.tooltip, styles.noBoxShadow)}
                >
                  See the list of devices
                </Tooltip>
              )}
            </Fragment>
          )
        })}
      {showOrderItemsModal && (
        <OrderItemsModal
          orderNumber={orderNumber}
          isVendor={!!isVendor}
          isWHManager={!!isWHManager}
          isPackedStatus={isPackedStatus}
          quantity={orderItem.quantity}
          productType={orderItem.product_type}
          onClose={() => setShowOrderItemsModal(false)}
        />
      )}
    </Container>
  )
}

export default OrdersQuantity
